import type { NavigationGuardWithThis, RouteLocation, RouteLocationNormalized } from 'vue-router';

import { defineAsyncComponent } from 'vue';

import type { PermissionObject } from '@/types/auth.types';

import instagramConstants from '@/app/analytics/instagram/constants';
import { competitiveRouteNames } from '@/app/competitive/constants';
import LDRouteGuard from '@/components/core/LDRouteGuard';
import { constants } from '@/config';
import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useIdentityStore } from '@/stores/identity';

const App = () => import('@/app/analytics/App.vue');

// Shared pages
const Overview = () => import('@/app/analytics/pages/ChannelOverviewPage.vue');
const AnalyticsYourPostsPage = () => import('@/app/analytics/pages/AnalyticsYourPostsPage.vue');
const BoardsIndex = () => import('@/app/boards/pages/BoardsIndex.vue');
const LikeShop = () => import('@/app/ecommerce/pages/LikeShop.vue');

// Instagram
const AccountOverview = () =>
  import('@/app/analytics/instagram/pages/Insights/AccountOverview.vue');
const CompetitiveClusterDetail = () =>
  import('@/app/analytics/instagram/pages/CompetitiveClusterDetail.vue');
const ContentRights = () => import('@/app/analytics/instagram/pages/UGC/ContentRights.vue');
const ContentRightsSettings = () =>
  import('@/app/analytics/instagram/pages/UGC/ContentRightsSettings.vue');
const HashtagMonitor = () => import('@/app/analytics/instagram/pages/UGC/HashtagMonitor.vue');
const InfluentialFans = () =>
  import('@/app/analytics/instagram/pages/Insights/InfluentialFans.vue');
const OrganicReach = () => import('@/app/analytics/instagram/pages/UGC/OrganicReach.vue');
const TrendEdit = () => import('@/app/analytics/instagram/components/TrendEdit.vue');
const ViqCompetitive = () => import('@/app/analytics/instagram/pages/ViqCompetitive.vue');
const ViqOwned = () => import('@/app/analytics/instagram/pages/ViqOwned.vue');
const ViqTrend = () => import('@/app/analytics/instagram/pages/ViqTrend.vue');
const ViqTrends = () => import('@/app/analytics/instagram/pages/ViqTrends.vue');
const ViqUgc = () => import('@/app/analytics/instagram/pages/ViqUgc.vue');
const YourFollowers = () => import('@/app/analytics/instagram/pages/Insights/YourFollowers.vue');
const StoryNarrative = () => import('@/app/analytics/instagram/pages/Stories/Narrative.vue');
const StoryMetricsReport = () =>
  import('@/app/analytics/instagram/pages/Stories/StoryMetricsReport.vue');
const StoryOverviewOrBoardsReport = () =>
  import('@/app/analytics/instagram/pages/Stories/StoryOverviewOrBoardsReport.vue');
const StoryFramesReport = () =>
  import('@/app/analytics/instagram/pages/Stories/StoryFramesReport.vue');

// Pinterest
const PinterestFollowers = () => import('@/app/analytics/pinterest/pages/Followers.vue');
const PinterestEngagement = () => import('@/app/analytics/pinterest/pages/Engagement.vue');

// TikTok
const TikTokUgc = () => import('@/app/analytics/tiktok/pages/Ugc.vue');
const TrendingSounds = defineAsyncComponent(
  () => import('@/app/analytics/tiktok/pages/TrendingSounds.vue'),
);
const TrendingHashtags = () => import('@/app/analytics/tiktok/pages/TrendingHashtags.vue');

const PAGE_LEVEL_TOP = {
  FACEBOOK: `Facebook`,
  INSTAGRAM: `Instagram`,
  LINKEDIN: 'LinkedIn',
  PINTEREST: `Pinterest`,
  SNAPCHAT: 'Snapchat',
  THREADS: 'Threads',
  TIKTOK: `TikTok`,
  TWITTER: `Twitter`,
  YOUTUBE: 'YouTube',
};
const PAGE_LEVEL_VIQ = `Visual IQ`;
const PAGE_LEVEL_TRENDS = 'Trends';
const PAGE_LEVEL_UGC = `UGC`;
const PAGE_LEVEL_BOARDS = `Boards`;
const PAGE_LEVEL_YOUR_STORIES = `Your Stories`;
const PAGE_LEVEL_INSIGHTS = `Insights`;
const PAGE_LEVEL_DH_BOARDS = `DH Boards`;

// Sends users without the permissions to Instagram's landing page. Checks the brand in the URL:
// the router doesn't switch the current brand when leaving a dashboard, so it can be stale here.
function requireInstagramPermissions(
  permissions: PermissionObject[],
): NavigationGuardWithThis<undefined> {
  return (to) => {
    const identityStore = useIdentityStore();
    const brandLabel = String(to.params.brandLabel);
    const targetBrand = identityStore.identity?.brands?.[brandLabel] ?? null;
    if (identityStore.guard(permissions, targetBrand)) return true;
    return { name: 'instagram', params: { brandLabel } };
  };
}

const facebookRoutes = {
  path: 'facebook',
  component: App,
  meta: {
    analyticsChannel: constants.FACEBOOK,
    requiresAuth: true,
  },
  children: [
    {
      name: 'facebook',
      path: '',
      redirect: { name: 'facebook.overview' },
    },
    {
      name: 'facebook.dashboard',
      path: 'dashboard',
      component: Overview,
      redirect: { name: 'facebook.overview' },
    },
    {
      name: 'facebook.dashboard.report',
      path: 'dashboard/report',
      component: Overview,
      meta: {
        report: true,
      },
      redirect: { name: 'facebook.overview.report' },
    },
    {
      name: 'facebook.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.FACEBOOK, key: constants.FACEBOOK },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.FACEBOOK} - Overview`,
      },
    },
    {
      name: 'facebook.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.FACEBOOK, key: constants.FACEBOOK },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.FACEBOOK} - Overview Report`,
        report: true,
      },
    },
    {
      name: 'facebook.yourPosts',
      path: 'your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.FACEBOOK,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.FACEBOOK} - Your Posts`,
      },
    },
    {
      name: 'facebook.yourPosts.report',
      path: 'your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.FACEBOOK,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.FACEBOOK} - Your Posts Report`,
        report: true,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'facebook.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.FACEBOOK,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.FACEBOOK} - Boards`,
          },
        },
      ],
    },
  ],
};

const instagramRoutes = {
  path: 'instagram',
  component: App,
  meta: {
    analyticsChannel: constants.INSTAGRAM,
    analytics: PAGE_LEVEL_TOP.INSTAGRAM,
    requiresAuth: true,
  },
  children: [
    {
      name: 'instagram',
      path: '',
      redirect: { name: 'instagram.overview' },
    },
    {
      name: 'instagram.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.INSTAGRAM, key: constants.INSTAGRAM },
      meta: {
        analytics: 'Instagram - Overview',
      },
    },
    {
      name: 'instagram.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.INSTAGRAM, key: constants.INSTAGRAM },
      meta: {
        report: true,
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - Overview - Report`,
      },
    },
    {
      name: 'instagram.viq.owned',
      path: 'viq/owned',
      component: ViqOwned,
      beforeEnter: requireInstagramPermissions([
        USER.VISION.CAN_ACCESS_VIQ,
        BRAND.VISION.CAN_ACCESS_VIQ,
      ]),
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - VIQ Owned`,
      },
    },
    {
      name: 'instagram.media',
      path: 'media/:id',
      redirect: (to: RouteLocation) => ({
        name: 'library.media',
        params: to.params,
        query: { ...to.query, source: 'INSTAGRAM' },
        hash: to.hash,
      }),
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - View Media`,
      },
    },
    {
      name: 'instagram.viq.competitive',
      path: 'viq/competitive',
      component: ViqCompetitive,
      // Competitive VIQ lives under Competitive now. A guard rather than `redirect` so that
      // CompetitiveClusterDetail's leave hook still sees this route as the destination.
      beforeEnter: (to: RouteLocationNormalized) => ({
        name: competitiveRouteNames.instagram.viq.main,
        params: { brandLabel: to.params.brandLabel },
      }),
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - List VIQ Competitive`,
      },
    },
    {
      name: 'instagram.viq.competitive.detail',
      path: 'viq/competitive/:id',
      component: CompetitiveClusterDetail,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - View VIQ Competitive`,
      },
    },
    {
      path: 'viq/trends',
      children: [
        {
          name: 'instagram.viq.trends',
          path: '',
          component: ViqTrends,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - View VIQ Trends`,
          },
          children: [
            {
              name: 'instagram.viq.trend.new',
              path: 'new',
              component: TrendEdit,
              meta: {
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_VIQ} - ${PAGE_LEVEL_TRENDS} - New`,
              },
            },
          ],
        },
        {
          name: 'instagram.viq.trend',
          path: ':id',
          component: ViqTrend,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - View VIQ Trend Detail`,
          },
          children: [
            {
              name: 'instagram.viq.trend.edit',
              path: 'edit',
              component: TrendEdit,
              meta: {
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_VIQ} - ${PAGE_LEVEL_TRENDS} - Edit`,
              },
            },
          ],
        },
      ],
    },
    {
      name: 'instagram.viq.trend.report',
      path: 'viq/trends/:id/report',
      component: ViqTrend,
      meta: {
        report: true,
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_VIQ} - ${PAGE_LEVEL_TRENDS} - Detail Report`,
      },
    },
    {
      name: 'instagram.viq.ugc',
      path: 'viq/ugc',
      component: ViqUgc,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - VIQ UGC`,
      },
    },
    {
      name: 'instagram.ugc.hashtag',
      path: 'ugc/hashtag',
      component: HashtagMonitor,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_UGC} - Hashtag`,
      },
    },
    {
      name: 'instagram.ugc.reach',
      path: 'ugc/reach',
      component: OrganicReach,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_UGC} - Organic Reach`,
      },
    },
    {
      name: 'instagram.ugc.content-rights',
      path: 'ugc/content-rights',
      component: ContentRights,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_UGC} - Content Rights`,
      },
    },
    {
      name: 'instagram.ugc.content-rights-settings',
      path: 'ugc/content-rights-settings',
      component: ContentRightsSettings,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_UGC} - Content Rights Settings`,
      },
    },
    {
      name: 'instagram.ugc.comments',
      path: 'ugc/comments/:postId',
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - Instagram UGC comment`,
        modal: {
          component: 'InstagramUgcCommentPopup',
        },
      },
    },
    {
      name: 'instagram.likeshop',
      path: 'likeshop',
      component: LikeShop,
      beforeEnter: requireInstagramPermissions([USER.INSTAGRAM.CAN_ACCESS_LIKESHOP]),
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - LikeShop`,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'instagram.galleries',
          path: '',
          component: BoardsIndex,
          beforeEnter: requireInstagramPermissions([USER.INSTAGRAM.CAN_ACCESS_BOARDS]),
          props: {
            galleryType: constants.INSTAGRAM,
            key: constants.INSTAGRAM,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_BOARDS}`,
          },
        },
      ],
    },
    {
      name: 'instagram.insights.accountOverview',
      path: 'insights/account-overview',
      component: AccountOverview,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - Account Overview`,
      },
    },
    {
      name: 'instagram.insights.accountOverview.report',
      path: 'insights/account-overview/report',
      component: AccountOverview,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_INSIGHTS} - Account Overview Report`,
        report: true,
      },
    },
    {
      name: 'instagram.insights.yourFollowers',
      path: 'insights/your-followers',
      component: YourFollowers,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - Your Followers`,
      },
    },
    {
      name: 'instagram.insights.influentialFans',
      path: 'insights/influential-fans',
      component: InfluentialFans,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - Influential Fans`,
      },
    },
    {
      name: 'instagram.insights.yourPosts',
      path: 'insights/your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.INSTAGRAM,
        key: constants.INSTAGRAM,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - Your Posts`,
      },
    },
    {
      name: 'instagram.insights.yourPosts.report',
      path: 'insights/your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.INSTAGRAM,
        key: constants.INSTAGRAM,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_INSIGHTS} - Your Posts Report`,
        report: true,
      },
    },
    {
      path: 'your-stories',
      children: [
        {
          path: 'story-overview',
          children: [
            {
              name: instagramConstants.routeNames.STORY_NARRATIVE,
              path: ':id',
              component: StoryNarrative,
              meta: {
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Overview - Detail`,
                narrativeRoute: true,
              },
            },
            {
              name: instagramConstants.routeNames.STORY_NARRATIVE_POPUP,
              path: ':id',
              component: StoryNarrative,
              meta: {
                modal: {
                  component: 'StoryBoardPopup',
                },
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Overview - Detail`,
                narrativeRoute: true,
              },
            },
            {
              name: instagramConstants.routeNames.STORY_OVERVIEW,
              path: '',
              component: Overview,
              props: { channel: 'INSTAGRAM_STORIES', key: constants.INSTAGRAM_STORY },
              meta: {
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Overview`,
              },
            },
            {
              name: `${instagramConstants.routeNames.STORY_OVERVIEW}.report`,
              path: 'report',
              component: Overview,
              props: { channel: 'INSTAGRAM_STORIES', key: constants.INSTAGRAM_STORY },
              meta: {
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Overview Report`,
                report: true,
              },
            },
          ],
        },
        {
          name: instagramConstants.routeNames.STORY_METRICS_REPORT,
          path: 'story-metrics-report',
          component: StoryMetricsReport,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Metrics Report`,
            report: true,
          },
        },
        {
          name: instagramConstants.routeNames.STORY_OVERVIEW_REPORT,
          path: 'story-overview-report',
          component: StoryOverviewOrBoardsReport,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Overview Report`,
            report: true,
            galleryType: constants.INSTAGRAM_STORY_NARRATIVE,
          },
        },
        {
          name: instagramConstants.routeNames.STORY_FRAMES,
          path: 'story-frames',
          component: AnalyticsYourPostsPage,
          props: {
            channel: constants.INSTAGRAM_STORY,
            key: constants.INSTAGRAM_STORY,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Frames`,
          },
        },
        {
          name: 'instagram.yourStories.storyFrames.report',
          path: 'story-frames/report',
          component: AnalyticsYourPostsPage,
          props: {
            channel: constants.INSTAGRAM_STORY,
            key: constants.INSTAGRAM_STORY,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Frames Report`,
            report: true,
            hideNav: true,
            hideVisionAi: true,
          },
        },
        {
          path: 'story-boards',
          children: [
            {
              name: instagramConstants.routeNames.STORY_BOARDS,
              path: '',
              component: BoardsIndex,
              props: {
                galleryType: constants.INSTAGRAM_STORY,
                key: constants.INSTAGRAM_STORY,
              },
              meta: {
                analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Boards`,
              },
            },
          ],
        },
        {
          path: 'story-boards/report',
          component: StoryOverviewOrBoardsReport,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story Boards Report`,
            report: true,
            galleryType: constants.INSTAGRAM_STORY,
          },
        },
        {
          name: instagramConstants.routeNames.STORY_NARRATIVE_REPORT,
          path: 'story/:id/report',
          component: StoryFramesReport,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Story - Detail Report`,
            report: true,
          },
        },
        {
          name: instagramConstants.routeNames.STORY_BOARD_REPORT,
          path: 'board/:id/report',
          component: StoryFramesReport,
          meta: {
            analytics: `${PAGE_LEVEL_TOP.INSTAGRAM} - ${PAGE_LEVEL_YOUR_STORIES} - Board - Detail Report`,
            report: true,
          },
        },
      ],
    },
  ],
};

const linkedinRoutes = {
  path: 'linkedin',
  component: App,
  meta: {
    analyticsChannel: constants.LINKEDIN,
    requiresAuth: true,
  },
  children: [
    {
      name: 'linkedin',
      path: '',
      redirect: { name: 'linkedin.overview' },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.LINKEDIN} - Overview`,
      },
    },
    {
      name: 'linkedin.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.LINKEDIN, key: constants.LINKEDIN },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.LINKEDIN} - Overview`,
      },
    },
    {
      name: 'linkedin.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.LINKEDIN, key: constants.LINKEDIN },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.LINKEDIN} - Overview Report`,
        report: true,
      },
    },
    {
      name: 'linkedin.yourPosts',
      path: 'your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.LINKEDIN,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.LINKEDIN} - Your Posts`,
      },
    },
    {
      name: 'linkedin.yourPosts.report',
      path: 'your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.LINKEDIN,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.LINKEDIN} - Your Posts Report`,
        report: true,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'linkedin.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.LINKEDIN,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.LINKEDIN} - ${PAGE_LEVEL_BOARDS}`,
          },
        },
      ],
    },
  ],
};

const pinterestRoutes = {
  path: 'pinterest',
  component: App,
  meta: {
    analyticsChannel: constants.PINTEREST,
    requiresAuth: true,
    permissions: [USER.PINTEREST.CAN_ACCESS_PINT],
  },
  children: [
    {
      name: 'pinterest',
      path: '',
      meta: {
        analytics: PAGE_LEVEL_TOP.PINTEREST,
      },
      redirect: { name: 'pinterest.index.overview' },
    },
    {
      name: 'pinterest.index',
      path: 'dashboard',
      component: Overview,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - Overview`,
        pinterestRoute: true,
      },
      redirect: { name: 'pinterest.index.overview' },
    },
    {
      name: 'pinterest.index.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.PINTEREST, key: constants.PINTEREST },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - Overview`,
        pinterestRoute: true,
      },
    },
    {
      name: 'pinterest.index.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.PINTEREST, key: constants.PINTEREST },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - Overview Report`,
        report: true,
      },
    },
    {
      name: 'pinterest.insights.engagement',
      path: 'engagement',
      component: PinterestEngagement,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_INSIGHTS} - Your Engagement`,
        pinterestRoute: true,
      },
    },
    {
      name: 'pinterest.insights.engagement.report',
      path: 'engagement/report',
      component: PinterestEngagement,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_INSIGHTS} - Your Engagement Report`,
        report: true,
      },
    },
    {
      name: 'pinterest.insights.followers',
      path: 'followers',
      component: PinterestFollowers,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_INSIGHTS} - Your Followers`,
        pinterestRoute: true,
      },
    },
    {
      name: 'pinterest.insights.followers.report',
      path: 'followers/report',
      component: PinterestFollowers,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_INSIGHTS} - Your Followers Report`,
        report: true,
        layout: 'horizontal',
      },
    },
    {
      name: 'pinterest.insights.pins',
      path: 'pins',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.PINTEREST,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_INSIGHTS} - Your Pins`,
        pinterestRoute: true,
      },
    },
    {
      name: 'pinterest.insights.pins.report',
      path: 'pins/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.PINTEREST,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_INSIGHTS} - Your Pins Report`,
        report: true,
      },
    },
    {
      path: 'board',
      children: [
        {
          name: 'pinterest.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.PINTEREST,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.PINTEREST} - ${PAGE_LEVEL_DH_BOARDS}`,
            pinterestRoute: true,
            permissions: [USER.PINTEREST.CAN_ACCESS_PINT_BOARDS],
          },
        },
      ],
    },
  ],
};

const snapchatRoutes = {
  path: 'snapchat',
  component: App,
  meta: {
    analyticsChannel: constants.SNAPCHAT,
    requiresAuth: true,
  },
  children: [
    {
      name: 'snapchat',
      path: '',
      redirect: { name: 'snapchat.overview' },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.SNAPCHAT} - Overview`,
      },
    },
    {
      name: 'snapchat.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.SNAPCHAT, key: constants.SNAPCHAT },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.SNAPCHAT} - Overview`,
      },
    },
    {
      name: 'snapchat.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.SNAPCHAT, key: constants.SNAPCHAT },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.SNAPCHAT} - Overview Report`,
        report: true,
      },
    },
    {
      name: 'snapchat.yourPosts',
      path: 'your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.SNAPCHAT,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.SNAPCHAT} - Your Posts`,
      },
    },
    {
      name: 'snapchat.yourPosts.report',
      path: 'your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.SNAPCHAT,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.SNAPCHAT} - Your Posts Report`,
        report: true,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'snapchat.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.SNAPCHAT,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.SNAPCHAT} - ${PAGE_LEVEL_BOARDS}`,
          },
        },
      ],
    },
  ],
};

const threadsRoutes = {
  path: 'threads',
  component: App,
  meta: {
    analyticsChannel: constants.THREADS,
    requiresAuth: true,
  },
  children: [
    {
      name: 'threads',
      path: '',
      redirect: { name: 'threads.overview' },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.THREADS} - Overview`,
      },
    },
    {
      name: 'threads.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.THREADS, key: constants.THREADS },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.THREADS} - Overview`,
      },
    },
    {
      name: 'threads.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.THREADS, key: constants.THREADS },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.THREADS} - Overview Report`,
        report: true,
      },
    },
    {
      name: 'threads.yourPosts',
      path: 'your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.THREADS,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.THREADS} - Your Posts`,
      },
    },
    {
      name: 'threads.yourPosts.report',
      path: 'your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.THREADS,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.THREADS} - Your Posts Report`,
        report: true,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'threads.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.THREADS,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.THREADS} - Boards`,
          },
        },
      ],
    },
  ],
};

const tiktokRoutes = {
  path: 'tiktok',
  component: App,
  meta: {
    analyticsChannel: constants.TIKTOK,
    requiresAuth: true,
  },
  children: [
    {
      name: 'tiktok',
      path: '',
      redirect: { name: 'tiktok.overview' },
    },
    {
      name: 'tiktok.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.TIKTOK, key: constants.TIKTOK },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Overview`,
      },
    },
    {
      name: 'tiktok.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.TIKTOK, key: constants.TIKTOK },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Overview Report`,
        report: true,
      },
    },
    {
      name: 'tiktok.yourPosts',
      path: 'your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.TIKTOK,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Your Posts`,
      },
    },
    {
      name: 'tiktok.yourPosts.report',
      path: 'your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.TIKTOK,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Your Posts Report`,
        report: true,
      },
    },
    {
      name: 'tiktok.ugc',
      path: 'ugc',
      component: TikTokUgc,
      meta: {
        skipConnectionCheck: true,
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - UGC`,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'tiktok.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.TIKTOK,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Boards`,
          },
        },
      ],
    },
    {
      name: 'tiktok.trendingSounds',
      path: 'trending-sounds',
      component: LDRouteGuard,
      props: {
        component: TrendingSounds,
        requiredFeatureFlag: 'tiktokTrendingSounds',
        to: { name: 'tiktok.overview' },
      },
      meta: {
        skipConnectionCheck: true,
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Trending Sounds`,
      },
    },
    {
      name: 'tiktok.trendingHashtags',
      path: 'trending-hashtags',
      component: TrendingHashtags,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - Trending Hashtags`,
      },
    },
    {
      name: 'tiktok.likeshop',
      path: 'likeshop',
      component: LikeShop,
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TIKTOK} - LikeShop`,
      },
    },
  ],
};

const twitterRoutes = {
  path: 'x',
  component: App,
  meta: {
    analyticsChannel: constants.TWITTER,
    requiresAuth: true,
  },
  children: [
    {
      name: 'twitter',
      path: '',
      redirect: { name: 'twitter.overview' },
    },
    {
      name: 'twitter.dashboard',
      path: 'dashboard',
      component: Overview,
      redirect: { name: 'twitter.overview' },
    },
    {
      name: 'twitter.dashboard.report',
      path: 'dashboard/report',
      component: Overview,
      meta: {
        report: true,
      },
      redirect: { name: 'twitter.overview.report' },
    },
    {
      name: 'twitter.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.TWITTER, key: constants.TWITTER },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TWITTER} - Overview`,
      },
    },
    {
      name: 'twitter.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.TWITTER, key: constants.TWITTER },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TWITTER} - Overview Report`,
        report: true,
      },
    },
    {
      path: 'your-tweets',
      redirect: {
        name: 'twitter.yourTweets',
      },
    },
    {
      name: 'twitter.yourTweets',
      path: 'your-posts',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.TWITTER,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TWITTER} - Your Posts`,
      },
    },
    {
      name: 'twitter.yourTweets.report',
      path: 'your-posts/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.TWITTER,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.TWITTER} - Your Posts Report`,
        report: true,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'twitter.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.TWITTER,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.TWITTER} - Boards`,
          },
        },
      ],
    },
  ],
};

const youtubeRoutes = {
  path: 'youtube',
  component: App,
  meta: {
    analyticsChannel: constants.YOUTUBE,
    requiresAuth: true,
  },
  children: [
    {
      name: 'youtube',
      path: '',
      redirect: { name: 'youtube.overview' },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.YOUTUBE} - Overview`,
      },
    },
    {
      name: 'youtube.overview',
      path: 'overview',
      component: Overview,
      props: { channel: constants.YOUTUBE, key: constants.YOUTUBE },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.YOUTUBE} - Overview`,
      },
    },
    {
      name: 'youtube.overview.report',
      path: 'overview/report',
      component: Overview,
      props: { channel: constants.YOUTUBE, key: constants.YOUTUBE },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.YOUTUBE} - Overview Report`,
        report: true,
      },
    },
    {
      path: 'boards',
      children: [
        {
          name: 'youtube.galleries',
          path: '',
          component: BoardsIndex,
          props: {
            galleryType: constants.YOUTUBE,
          },
          meta: {
            analytics: `${PAGE_LEVEL_TOP.YOUTUBE} - Boards`,
          },
        },
      ],
    },
    {
      name: 'youtube.yourVideos',
      path: 'your-videos',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.YOUTUBE,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.YOUTUBE} - Your Videos`,
      },
    },
    {
      name: 'youtube.yourVideos.report',
      path: 'your-videos/report',
      component: AnalyticsYourPostsPage,
      props: {
        channel: constants.YOUTUBE,
      },
      meta: {
        analytics: `${PAGE_LEVEL_TOP.YOUTUBE} - Your Videos Report`,
        report: true,
      },
    },
  ],
};

export default [
  facebookRoutes,
  instagramRoutes,
  linkedinRoutes,
  pinterestRoutes,
  snapchatRoutes,
  threadsRoutes,
  tiktokRoutes,
  twitterRoutes,
  youtubeRoutes,
];
